<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'

import { cn } from '../../../lib/utils'
import type { Testable } from '../../../main'

export interface TextAreaProperties extends Testable {
  class?: HTMLAttributes['class']
  /** @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token_list_tokens */
  autocomplete?: boolean | string | undefined
  disabled?: boolean
  rows?: number
  error?: string
  limit?: number
  autofocus?: boolean
  readonly?: boolean
}

const model = defineModel<string | number>()

const computedAutocomplete = computed(() => {
  if (typeof properties.autocomplete === 'boolean') {
    return properties.autocomplete ? 'on' : 'off'
  }

  return properties.autocomplete
})

const properties = defineProps<TextAreaProperties>()
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/no-autofocus -->
  <!-- eslint-disable vuejs-accessibility/form-control-has-label -->
  <textarea
    v-model="model"
    :autocomplete="computedAutocomplete"
    :autofocus
    :class="[cn('atabix-textarea', properties.class), { 'atabix-textarea--error': !!properties.error }]"
    :data-test
    :disabled
    :limit
    :rows="rows || 3"
  />
</template>

<style lang="scss">
.atabix-textarea {
  @apply flex;
  @apply min-h-20;
  @apply w-full;
  @apply rounded-md;
  @apply border;
  @apply border-border;
  @apply bg-background;
  @apply px-3;
  @apply py-2;
  @apply text-base;
  @apply placeholder:text-foreground-placeholder;
  @apply focus-visible:outline-none;
  @apply focus-visible:border-form-border-selected;
  @apply disabled:cursor-not-allowed;
  @apply disabled:opacity-50;
}

.atabix-textarea--error {
  @apply border-form-border-error;
}
</style>
